import { useState, Fragment, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faRotateLeft,
  faArrowRight,
  faClipboardList,
} from '@fortawesome/free-solid-svg-icons';
import {
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
  Skeleton,
  Paginator,
} from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { useAuditLog, useRevertAudit, useAuditUsers, type AuditLogEntry } from '@/api/useAuditLog';
import styles from './AdminActions.module.css';

const MODEL_OPTIONS = [
  { value: '', label: 'All Models' },
  { value: 'Provider', label: 'Provider' },
  { value: 'ProviderService', label: 'ProviderService' },
  { value: 'ServiceSpecification', label: 'ServiceSpecification' },
  { value: 'ServiceCost', label: 'ServiceCost' },
  { value: 'Presentation', label: 'Presentation' },
  { value: 'ProviderUser', label: 'ProviderUser' },
  { value: 'TokenUsage', label: 'TokenUsage' },
];

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'revert', label: 'Revert' },
];

const PAGE_SIZE = 15;

function ActionBadge({ action }: { action: AuditLogEntry['action'] }) {
  return <span className={`${styles.actionBadge} ${styles[`badge-${action}`]}`}>{action}</span>;
}

function ChangesDiff({
  changes,
}: {
  changes: Record<string, { old: unknown; new: unknown }> | null | undefined;
}) {
  if (!changes || typeof changes !== 'object') {
    return <span className={styles.noChanges}>No field changes recorded</span>;
  }
  const entries = Object.entries(changes);
  if (entries.length === 0) {
    return <span className={styles.noChanges}>No field changes recorded</span>;
  }
  return (
    <div className={styles.changesList}>
      {entries.map(([field, { old: oldVal, new: newVal }]) => (
        <div key={field} className={styles.changeItem}>
          <span className={styles.fieldName}>{field}</span>
          <span className={styles.oldValue}>{JSON.stringify(oldVal)}</span>
          <FontAwesomeIcon icon={faArrowRight} className={styles.arrow} />
          <span className={styles.newValue}>{JSON.stringify(newVal)}</span>
        </div>
      ))}
    </div>
  );
}

function ExpandedRowContent({
  entry,
  onRevert,
  isReverting,
}: {
  entry: AuditLogEntry;
  onRevert: (id: string) => void;
  isReverting: boolean;
}) {
  const canRevert = entry.action === 'update' || entry.action === 'delete';

  return (
    <div className={styles.expandedRow}>
      <div className={styles.expandedGrid}>
        <div className={styles.expandedSection}>
          <h4 className={styles.expandedSectionTitle}>Field Changes</h4>
          <ChangesDiff changes={entry.changes} />
        </div>

        <div className={styles.expandedSection}>
          <h4 className={styles.expandedSectionTitle}>Details</h4>
          <div className={styles.detailsList}>
            {entry.ip_address && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>IP Address</span>
                <span className={styles.detailValue}>{entry.ip_address}</span>
              </div>
            )}
            {entry.user_agent && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>User Agent</span>
                <span
                  className={styles.detailValue + ' ' + styles.truncated}
                  title={entry.user_agent}
                >
                  {entry.user_agent}
                </span>
              </div>
            )}
            {entry.reverted_from !== null && (
              <div className={styles.detailItem}>
                <span className={styles.detailLabel}>Reverted From</span>
                <span className={styles.detailValue}>Entry #{entry.reverted_from}</span>
              </div>
            )}
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Object ID</span>
              <span className={styles.detailValue}>{entry.object_id}</span>
            </div>
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Object</span>
              <span className={styles.detailValue}>{entry.object_repr}</span>
            </div>
          </div>
        </div>
      </div>

      {canRevert && (
        <div className={styles.revertContainer}>
          <button
            className={styles.revertBtn}
            onClick={() => onRevert(entry.id)}
            disabled={isReverting}
          >
            <FontAwesomeIcon icon={faRotateLeft} />
            {isReverting ? 'Reverting...' : 'Revert this change'}
          </button>
        </div>
      )}
    </div>
  );
}

function formatTimestamp(ts: string) {
  return new Date(ts).toLocaleString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function summarizeChanges(
  changes: Record<string, { old: unknown; new: unknown }> | null | undefined,
): string {
  if (!changes || typeof changes !== 'object') return '—';
  const count = Object.keys(changes).length;
  if (count === 0) return '—';
  const fields = Object.keys(changes).slice(0, 3).join(', ');
  return count > 3 ? `${fields} +${count - 3} more` : fields;
}

export function AdminActions() {
  const [modelFilter, setModelFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [objectIdSearch, setObjectIdSearch] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [textSearch, setTextSearch] = useState('');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const filters: Record<string, string> = {};
  if (modelFilter) filters.model_name = modelFilter;
  if (actionFilter) filters.action = actionFilter;
  if (objectIdSearch) filters.object_id = objectIdSearch;
  if (userIdFilter) filters.user_id = userIdFilter;
  if (dateFrom) filters.date_from = dateFrom;
  if (dateTo) filters.date_to = dateTo;
  if (textSearch.trim()) filters.search = textSearch.trim();

  const activeFilters = Object.keys(filters).length > 0 ? filters : undefined;

  // Reset to page 1 whenever any filter changes
  useEffect(() => {
    setPage(1);
  }, [modelFilter, actionFilter, objectIdSearch, userIdFilter, dateFrom, dateTo, textSearch]);

  const { data, isLoading, isError } = useAuditLog(activeFilters, page);
  const { data: users } = useAuditUsers();
  const revertMutation = useRevertAudit();

  const entries = data?.results ?? [];
  const totalCount = data?.count ?? 0;
  const numPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const userOptions = [
    { value: '', label: 'All Users' },
    ...(users ?? []).map((u) => ({
      value: String(u.id),
      label: u.email,
    })),
  ];

  function handleRowClick(id: string) {
    setExpandedRowId((prev) => (prev === id ? null : id));
  }

  async function handleRevert(auditId: string) {
    const confirmed = window.confirm(
      'Are you sure you want to revert this change? This action cannot be undone.',
    );
    if (!confirmed) return;

    setRevertingId(auditId);
    try {
      await revertMutation.mutateAsync(auditId);
      toast.success('Change reverted successfully');
      setExpandedRowId(null);
    } catch {
      toast.error('Failed to revert change');
    } finally {
      setRevertingId(null);
    }
  }

  const renderSkeletonRows = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <TableRow key={`skeleton-${i}`}>
        <TableCell>
          <Skeleton width="130px" />
        </TableCell>
        <TableCell>
          <Skeleton width="120px" />
        </TableCell>
        <TableCell>
          <Skeleton width="70px" />
        </TableCell>
        <TableCell>
          <Skeleton width="100px" />
        </TableCell>
        <TableCell>
          <Skeleton width="60px" />
        </TableCell>
        <TableCell>
          <Skeleton width="160px" />
        </TableCell>
      </TableRow>
    ));

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <FontAwesomeIcon icon={faClipboardList} className={styles.headerIcon} />
          <div>
            <h1>Activity Log</h1>
            <p>Track all create, update, delete, and revert actions across the system</p>
          </div>
        </div>
      </div>

      <Card className={styles.tableCard}>
        <div className={styles.filters}>
          <Select
            label="Model"
            value={modelFilter}
            onChange={(e) => setModelFilter(e.target.value)}
            options={MODEL_OPTIONS}
          />
          <Select
            label="Action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            options={ACTION_OPTIONS}
          />
          <Select
            label="User"
            value={userIdFilter}
            onChange={(e) => setUserIdFilter(e.target.value)}
            options={userOptions}
          />
          <Input
            name="date-from"
            label="Date From"
            value={dateFrom}
            setValue={setDateFrom}
            placeholder="Start date"
            type="date"
            variant="bordered"
          />
          <Input
            name="date-to"
            label="Date To"
            value={dateTo}
            setValue={setDateTo}
            placeholder="End date"
            type="date"
            variant="bordered"
          />
          <Input
            name="object-id-search"
            label="Object ID"
            value={objectIdSearch}
            setValue={setObjectIdSearch}
            placeholder="e.g. 42"
            variant="bordered"
            isClearable
          />
          <Input
            name="text-search"
            label="Object Name"
            value={textSearch}
            setValue={setTextSearch}
            placeholder="Search by name..."
            variant="bordered"
            isClearable
          />
        </div>

        <div className={styles.tableContainer}>
          <Table>
            <TableHeader>
              <TableColumn>{''}</TableColumn>
              <TableColumn>Timestamp</TableColumn>
              <TableColumn>User</TableColumn>
              <TableColumn>Action</TableColumn>
              <TableColumn>Model</TableColumn>
              <TableColumn>Object</TableColumn>
              <TableColumn>Changed Fields</TableColumn>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                renderSkeletonRows()
              ) : isError ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className={styles.emptyState}>
                      Failed to load audit log. Please try again.
                    </div>
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className={styles.emptyState}>No activity recorded yet.</div>
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <Fragment key={entry.id}>
                    <TableRow
                      onClick={() => handleRowClick(entry.id)}
                      className={`${styles.dataRow} ${expandedRowId === entry.id ? styles.dataRowExpanded : ''}`}
                    >
                      <TableCell>
                        <FontAwesomeIcon
                          icon={expandedRowId === entry.id ? faChevronDown : faChevronRight}
                          className={styles.expandIcon}
                        />
                      </TableCell>
                      <TableCell>
                        <span className={styles.timestamp}>{formatTimestamp(entry.timestamp)}</span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.userEmail}>
                          {entry.user_email ?? <em className={styles.systemLabel}>system</em>}
                        </span>
                      </TableCell>
                      <TableCell>
                        <ActionBadge action={entry.action} />
                      </TableCell>
                      <TableCell>
                        <span className={styles.modelName}>{entry.model_name}</span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.objectRepr} title={entry.object_repr}>
                          {entry.object_repr}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={styles.changesSummary}>
                          {summarizeChanges(entry.changes)}
                        </span>
                      </TableCell>
                    </TableRow>

                    {expandedRowId === entry.id && (
                      <TableRow key={`${entry.id}-expanded`} className={styles.expandedTableRow}>
                        <TableCell colSpan={7}>
                          <ExpandedRowContent
                            entry={entry}
                            onRevert={handleRevert}
                            isReverting={revertingId === entry.id}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {!isLoading && !isError && totalCount > 0 && (
          <div className={styles.paginationBar}>
            <span className={styles.paginationInfo}>
              {totalCount} {totalCount === 1 ? 'entry' : 'entries'} — page {page} of {numPages}
            </span>
            <Paginator page={page} numPages={numPages} onPageChange={setPage} size="sm" />
          </div>
        )}
      </Card>
    </div>
  );
}
